import { Rectangle } from 'pixi.js';
import type { CanvasTextMetrics } from '../metrics/CanvasTextMetrics';
import { getTotalBounds, isBreakingSpace } from '../textMetricsHelpers';
import {
    DynamicInferredDimensions,
    DynamicLevel,
    DynamicXAlign,
    DynamicYAlign,
} from '../../../../enums';
import type { Pixels, RgbaString } from '../../../../types';
import { Config } from '../../../../video-data/config';
import { rgbArray2String, string2rgbaArray } from '../../../../libs/videoData';
import type {
    CanvasTextDynamicGraphicRendererSettingsBase,
    Position,
} from './CanvasTextDynamicGraphicRenderer';
import {
    WORD_LEVEL_ALIGN_Y_OPTIONS,
    CanvasTextDynamicGraphicRenderer,
} from './CanvasTextDynamicGraphicRenderer';
import type { CanvasTextDynamicGraphicAnimationRenderer } from './CanvasTextDynamicGraphicAnimationRenderer';

type CircleParameters = Position & {
    radius: Pixels;
    lineWidth: Pixels;
};

export interface CanvasTextDynamicCircleRendererSettings
    extends CanvasTextDynamicGraphicRendererSettingsBase {
    type: typeof Config.CIRCLE_GRAPHIC;
    radius: Pixels;
    strokeStyle: RgbaString;
    lineWidth: Pixels;
    fill: RgbaString;
}

type AnimatableCircleProperties =
    (typeof CanvasTextDynamicCircleRenderer.animatableProperties)[number];
export enum AnimatableGeometricCircleProperties {
    radius = 'radius',
}

/**
 * CanvasTextDynamicCircle is a class that renders a circle behind a block of text.
 */
export class CanvasTextDynamicCircleRenderer extends CanvasTextDynamicGraphicRenderer {
    override readonly type = Config.CIRCLE_GRAPHIC;
    radius = 0 as Pixels;
    strokeStyle = 'rgba(255,255,255,0)' as RgbaString;
    lineWidth = 0 as Pixels;
    fill: RgbaString = 'rgba(255,0,255,1)';

    /** Circles are position relative to their center. */
    #centerPositionsForLevel: Position[] = null;

    override animationConfigurations: CanvasTextDynamicGraphicAnimationRenderer<AnimatableCircleProperties>[] =
        [];

    override apply(settings: CanvasTextDynamicCircleRendererSettings) {
        super.apply(settings);
        this.radius = settings.radius;
        this.strokeStyle = settings.strokeStyle;
        this.lineWidth = settings.lineWidth;
        this.fill = settings.fill;
    }

    /**
     * Builds up a collection of center positions, one for each circle
     */
    override build(metrics: CanvasTextMetrics) {
        super.build(metrics);

        switch (this.level) {
            case DynamicLevel.object:
                this.#centerPositionsForLevel = this.#getObjectLevelPosition(metrics);
                break;
            case DynamicLevel.line:
                this.#centerPositionsForLevel = this.#getLineLevelPositions(metrics);
                break;
            case DynamicLevel.word:
                this.#centerPositionsForLevel = this.#getWordLevelPositions(metrics);
                break;
            case DynamicLevel.highlight:
                this.#centerPositionsForLevel = this.#getHighlightLevelPositions(metrics);
                break;
            default:
                throw new Error(`Invalid level ${this.level}`);
        }
    }

    #getObjectLevelPosition(metrics: CanvasTextMetrics): Position[] {
        return [
            {
                x: this.#getXPosition(metrics.bounds) + this.positionX,
                y: this.#getYPosition(metrics.bounds, metrics.ascent) + this.positionY,
            },
        ];
    }

    #getLineLevelPositions(metrics: CanvasTextMetrics): Position[] {
        return metrics.lines.map((line) => {
            return {
                x: this.#getXPosition(line.bounds) + this.positionX,
                y: this.#getYPosition(line.bounds, line.ascent) + this.positionY,
            };
        });
    }

    #getWordLevelPositions(metrics: CanvasTextMetrics): Position[] {
        const positions: Position[] = [];
        metrics.lines.forEach((line) => {
            line.words.forEach((word) => {
                if (isBreakingSpace(word.text)) {
                    return;
                }
                let boundsForY: Rectangle;
                if (WORD_LEVEL_ALIGN_Y_OPTIONS.includes(this.alignY)) {
                    // the unique word level alignment options use the line bounds instead
                    boundsForY = line.bounds;
                } else {
                    boundsForY = word.bounds;
                }
                positions.push({
                    x: this.#getXPosition(word.bounds) + this.positionX,
                    y: this.#getYPosition(boundsForY, line.ascent) + this.positionY,
                });
            });
        });

        return positions;
    }

    #getHighlightLevelPositions(metrics: CanvasTextMetrics): Position[] {
        const positions: Position[] = [];
        metrics.lines.forEach((line) => {
            line.highlightSpans.forEach((span) => {
                let boundsForY: Rectangle;
                if (WORD_LEVEL_ALIGN_Y_OPTIONS.includes(this.alignY)) {
                    boundsForY = line.bounds;
                } else {
                    boundsForY = span.bounds;
                }
                positions.push({
                    x: this.#getXPosition(span.bounds) + this.positionX,
                    y: this.#getYPosition(boundsForY, line.ascent) + this.positionY,
                });
            });
        });

        return positions;
    }

    #getXPosition({ x, width }: Rectangle): number {
        // The text bounds are relative to the top left while the circle is relative to its center.
        const circleTotalRadius = this.#getTotalRadius();
        switch (this.alignX) {
            case DynamicXAlign.left:
                x += circleTotalRadius;
                break;
            case DynamicXAlign.center:
                x += width * 0.5;
                break;
            case DynamicXAlign.right:
                x += width - circleTotalRadius;
                break;
        }
        return x;
    }

    #getYPosition({ y, height }: Rectangle, ascent: number): number {
        // The text bounds are relative to the top left while the circle is relative to its center.
        const circleTotalRadius = this.#getTotalRadius();
        switch (this.alignY) {
            case DynamicYAlign.maxTop:
            case DynamicYAlign.top:
                y += circleTotalRadius;
                break;
            case DynamicYAlign.center:
            case DynamicYAlign.averageCenter:
                y += height * 0.5;
                break;
            case DynamicYAlign.baseline:
                y += ascent - circleTotalRadius;
                break;
            case DynamicYAlign.bottom:
            case DynamicYAlign.maxBottom:
                y += height - circleTotalRadius;
                break;
        }
        return y;
    }

    /**
     * The radius defines the size of the circle, but then an outline is drawn on the circumference of that
     * circle using the line width. The line width is drawn half inside the circle and half outside the circle.
     * This means that the total radius of the circle is the radius plus half the line width.
     */
    #getTotalRadius(): number {
        return this.radius + this.lineWidth / 2;
    }

    override _getBounds() {
        const length = 2 * this.radius + this.lineWidth;
        const halfLength = length / 2;
        return getTotalBounds(
            this.#centerPositionsForLevel.map(
                ({ x, y }) => new Rectangle(x - halfLength, y - halfLength, length, length),
            ),
        );
    }

    static get animatableProperties() {
        return [
            ...CanvasTextDynamicGraphicRenderer.baseAnimatableProperties,
            ...Object.values(AnimatableGeometricCircleProperties),
        ];
    }

    static get animatableGeometricProperties() {
        return [
            ...CanvasTextDynamicGraphicRenderer.baseAnimatableGeometricProperties,
            ...Object.values(AnimatableGeometricCircleProperties),
        ];
    }

    override draw(context: CanvasRenderingContext2D) {
        this.#centerPositionsForLevel.forEach((_, index) => {
            const [position, radius, fill] = this._applyAnimationToIndex(index);
            drawCircle(
                context,
                { x: position.x, y: position.y, radius, lineWidth: this.lineWidth },
                fill,
                this.strokeStyle,
            );
        });
    }

    override clipElement(context: CanvasRenderingContext2D, elementIndex: number) {
        const path = new Path2D();
        const circlePath = new Path2D();
        const [position, radius] = this._applyAnimationToIndex(elementIndex);
        const radiusIncludingStroke = radius + this.lineWidth / 2;
        circlePath.arc(position.x, position.y, radiusIncludingStroke, 0, 2 * Math.PI);
        path.addPath(circlePath);
        context.clip(path);
    }

    /** Apply the animation to the bounds at the given index. This will return a new rectangle and fill color. */
    _applyAnimationToIndex(index: number): [Position, Pixels, RgbaString] {
        let tempRadius = this.radius;
        let { x, y } = this.#centerPositionsForLevel[index];
        let fill = this.fill;
        const [r, g, b] = string2rgbaArray(fill);
        this.animationConfigurations.forEach((animation) => {
            switch (animation.property) {
                case 'radius':
                    {
                        const offset = animation.shouldOffsetPropertyValue ? this.radius : 0;
                        tempRadius = (animation.computedValues[index] + offset) as Pixels;
                    }
                    break;
                case 'x':
                    {
                        const offset = animation.shouldOffsetPropertyValue ? x : 0;
                        x = animation.computedValues[index] + offset;
                    }
                    break;
                case 'y':
                    {
                        const offset = animation.shouldOffsetPropertyValue ? y : 0;
                        y = animation.computedValues[index] + offset;
                    }
                    break;
                case 'alpha':
                    fill = rgbArray2String([
                        r,
                        g,
                        b,
                        animation.computedValues[index],
                    ]) as RgbaString;
                    break;
            }
        });

        return [{ x, y }, tempRadius, fill];
    }

    override get _numberOfShapes(): number {
        return this.#centerPositionsForLevel.length;
    }

    override _getInferredValues(inferredDimension: DynamicInferredDimensions): number[] {
        switch (inferredDimension) {
            case DynamicInferredDimensions.height:
            case DynamicInferredDimensions.maxHeight:
            case DynamicInferredDimensions.width:
            case DynamicInferredDimensions.maxWidth:
                // The circle graphic doesn't support inferring radius from the text, so it's always the same value for
                // all shapes drawn by this graphic. Also since it's a circle, it means height & width are the same.
                return Array(this._numberOfShapes).fill(2 * this.#getTotalRadius());

            // The rest are not valid inferred dimensions when animating a circle
            case DynamicInferredDimensions.gapless:
            case DynamicInferredDimensions.heightWithoutDescent:
            case DynamicInferredDimensions.gaplessWidth:
            default:
                throw new Error(`Invalid inferred dimension ${inferredDimension}`);
        }
    }

    override _getOffsetForProperty(
        property: AnimatableCircleProperties,
        shouldOffsetPropertyValue: boolean,
    ): number {
        switch (property) {
            case 'x':
            case 'y':
            case 'radius':
                // offset is handled during draw
                return 0;
            case 'alpha':
                if (shouldOffsetPropertyValue) {
                    // During the canvas migration we didn't have any use cases of offsetting alpha for
                    // circles, so we're not adding support for it here. In general, it probably doesn't
                    // make sense to support offsetting alpha, so it would be nice to clean this up
                    // eventually, but for now just enforce that we can't try to use this combination.
                    throw new Error(`Property ${property} does not support offsetting.`);
                }
                return 0;
            default:
                throw new Error(`Unknown property ${property}`);
        }
    }
}

const drawCircle = (
    context: CanvasRenderingContext2D,
    params: CircleParameters,
    fillStyle: RgbaString,
    strokeStyle: RgbaString,
) => {
    const { x, y, lineWidth, radius } = params;
    context.fillStyle = fillStyle;
    context.strokeStyle = strokeStyle;
    context.lineWidth = lineWidth;
    context.beginPath();
    context.arc(x, y, radius, 0, 2 * Math.PI);
    context.fill();
    context.stroke();
};
